import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack, BiBuildings, BiMap, BiShapeSquare } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import { activeImportValues, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';
import './CustomerMaster.scss';

const GROUPS = [
  'Transport',
  'Structures',
  'Urban',
  'Water',
  'Rail'
];

const EMPTY = {
  sectorNo: '',
  code: '',
  name: '',
  group: 'Transport',
  typicalClients: '',
  assetFocus: '',
  description: '',
  notes: '',
  status: 'active'
};

const monogram = (form) => {
  const source = String(form.code || form.name || '').trim();
  if (!source) return 'New';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 4).toUpperCase();
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const SectorFormPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const { sectorId } = useParams();
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState(GROUPS);
  const revision = useMasterRevision({ entity: 'sector' });

  useDocumentTitle(isCreate ? 'New sector' : (form.name || 'Sector'));

  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const list = activeImportValues(data?.settings?.importLists?.sectorGroups);
      if (list.length) setGroups(list);
    });
  }, []);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await parivartanAPI.getSector(sectorId);
        const row = response.data?.data?.sector;
        if (!cancelled && row) {
          setForm({
            ...EMPTY,
            ...row,
            sectorNo: row.sectorNo || '',
            code: row.code || '',
            typicalClients: row.typicalClients || '',
            assetFocus: row.assetFocus || '',
            description: row.description || '',
            notes: row.notes || ''
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load this sector');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sectorId, isCreate]);

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.sectorNo) delete payload.sectorNo;
      if (!payload.code) delete payload.code;
      if (!isCreate) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) return;
        payload.changeReason = changeReason;
      }
      if (isCreate) await parivartanAPI.createSector(payload);
      else await parivartanAPI.updateSector(sectorId, payload);
      navigate('/apps/tender/masters/sectors');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this sector');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) {
    return (
      <div className="cust-page cust-page--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const mark = monogram(form);
  const heading = isCreate ? 'New sector' : (form.name || 'Sector');
  const locked = isView;

  return (
    <div className="cust-page">
      <form id="sector-master-form" className={`cust-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters/sectors')}>
              <BiArrowBack />
              Sectors
            </button>
            <div className="cust-mast__row">
              <span className={`cust-mark${mark.length > 2 ? ' cust-mark--wide' : ''}`} aria-hidden>{mark}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'Sector master' : isEdit ? 'Editing sector' : 'Sector master'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  {form.sectorNo ? <span className="cust-chip cust-chip--no">{form.sectorNo}</span> : <span className="cust-chip">Number on save</span>}
                  {form.code ? <span className="cust-chip">{form.code}</span> : null}
                  {form.group ? <span className="cust-chip">{form.group}</span> : null}
                  <span className={`cust-status cust-status--${form.status === 'active' ? 'active' : 'inactive'}`}>
                    {form.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/masters/sectors/${sectorId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" onClick={() => navigate('/apps/tender/masters/sectors')} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isCreate ? 'Create sector' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>The sector</h2>
                  <p>How this pursuit is named when a tender is captured.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Name" wide="8">
                  <Form.Control value={form.name} onChange={setField('name')} required disabled={locked} placeholder="Roads and Highways" />
                </Field>
                <Field label="Sector no." wide="4">
                  <Form.Control className="cust-input--code" value={form.sectorNo} onChange={setField('sectorNo')} placeholder="Assigned on save" disabled={locked} />
                </Field>
                <Field label="Code" wide="4">
                  <Form.Control className="cust-input--code" value={form.code} onChange={setField('code')} disabled={locked} placeholder="HWY" />
                </Field>
                <Field label="Group" wide="4">
                  <Form.Select value={form.group} onChange={setField('group')} disabled={locked}>
                    {keepCurrent(groups, form.group, GROUPS).map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </Field>
                <Field label="Status" wide="4">
                  <Form.Select value={form.status} onChange={setField('status')} disabled={locked}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Where it is pursued</h2>
                  <p>The authorities that invite this work, and the assets it covers.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Typical clients" wide="6">
                  <Form.Control value={form.typicalClients} onChange={setField('typicalClients')} disabled={locked} placeholder="NHAI, MSRDC, PWD Maharashtra" />
                </Field>
                <Field label="Assets" wide="6">
                  <Form.Control value={form.assetFocus} onChange={setField('assetFocus')} disabled={locked} placeholder="Expressways, national highways, and state roads" />
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>03</span>
                <div>
                  <h2>Scope</h2>
                  <p>What belongs in this sector, and what stays outside it.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Description" wide="12">
                  <Form.Control as="textarea" rows={3} value={form.description} onChange={setField('description')} disabled={locked} placeholder="The work this sector covers" />
                </Field>
                <Field label="Notes" wide="12">
                  <Form.Control as="textarea" rows={3} value={form.notes} onChange={setField('notes')} disabled={locked} placeholder="When to use this sector, or when to leave it inactive" />
                </Field>
              </div>
            </section>
          </div>

          <aside className="cust-rail">
            <div className="cust-rail__card">
              <p className="cust-rail__label">At a glance</p>
              <h2>{form.code || form.name || 'New sector'}</h2>
              <p className="cust-rail__place">{form.group || 'Group not set'}</p>
              <ul>
                <li>
                  <BiBuildings aria-hidden />
                  <span>{form.typicalClients || 'Clients not set'}</span>
                </li>
                <li>
                  <BiMap aria-hidden />
                  <span>{form.assetFocus || 'Assets not set'}</span>
                </li>
                <li>
                  <BiShapeSquare aria-hidden />
                  <span>{form.name || 'Name not set'}</span>
                </li>
              </ul>
            </div>
            {form.description ? (
              <div className="cust-rail__card cust-rail__notes">
                <p className="cust-rail__label">Scope</p>
                <p>{form.description}</p>
              </div>
            ) : null}
          </aside>
        </div>
      </form>
      {revision.modals}
    </div>
  );
};

export default SectorFormPage;
